import { useState, useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';
import Footer from './Footer';
import axios from 'axios';
import { getAuthPhotoUrl } from './AuthImage';
import { PhotoIdentite } from './PhotoIdentite';
import { 
  LayoutDashboard, 
  FileText, 
  Calendar, 
  TrendingUp, 
  Clock, 
  User, 
  ClipboardList, 
  CheckSquare, 
  Award, 
  Bell, 
  LogOut,
  Menu,
  X,
  Users,
  Anchor,
  BarChart3,
  History,
  Building2,
  ClipboardCheck,
  FileEdit,
  BookOpen,
  UserCheck,
  Shield,
  ArrowLeft,
  CalendarX,
  Play,
  Star,
  Briefcase,
  GraduationCap,
  Send,
  Download,
  Upload,
  Monitor,
  Newspaper,
  MessageSquare,
  Archive,
  ShieldCheck,
  MessageSquareWarning,
  ScrollText,
  Compass,
} from 'lucide-react';
import { Button } from './ui/button';
import GlobalSearch from './GlobalSearch';
import StructureSwitcher from './StructureSwitcher';

const roleLabelsMap = {
  centre: 'Centre',
  stagiaire: 'Stagiaire',
  formateur: 'Formateur',
  tuteur: 'Tuteur',
  entreprise: 'Entreprise',
  responsable_pedagogique: 'Resp. Pédagogique',
  moniteur: 'Moniteur'
};

const Layout = ({ children, user, onLogout, isImpersonating, onExitImpersonate, originalUser, onSwitchRole }) => {
  const location = useLocation();
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [unreadNotifications, setUnreadNotifications] = useState(0);

  const [unreadLettresMission, setUnreadLettresMission] = useState(0);
  const [guideOuvert, setGuideOuvert] = useState(false);

  // Onglet « Guide BC1 / BC2 » : ouvert ou fermé par le centre
  useEffect(() => {
    if (!['stagiaire', 'formateur', 'responsable_pedagogique'].includes(user?.role)) return;
    axios.get('/guide-bpjeps/acces').then(({ data }) => setGuideOuvert(data.acces)).catch(() => setGuideOuvert(false));
  }, [user?.role, location.pathname]);

  // Récupérer le nombre de notifications non lues
  useEffect(() => {
    const syncAppBadge = (count) => {
      // Badge sur l'icône PWA (Android Chrome, iOS 16.4+, desktop)
      try {
        if ('setAppBadge' in navigator) {
          if (count > 0) navigator.setAppBadge(count).catch(() => {});
          else navigator.clearAppBadge?.().catch(() => {});
        }
      } catch {}
      // Synchroniser également le service worker (au cas où la page serait fermée)
      try {
        if ('serviceWorker' in navigator && navigator.serviceWorker.controller) {
          navigator.serviceWorker.controller.postMessage({ type: 'SET_BADGE', count });
        }
      } catch {}
    };

    const fetchUnreadCount = async () => {
      if (!user) return;
      try {
        const response = await axios.get('/notifications/unread-count');
        const count = response.data?.count || 0;
        setUnreadNotifications(count);
        syncAppBadge(count);
      } catch (error) {
      }
      // Compteur spécifique lettres de mission pour formateur et centre
      if (user.role === 'formateur' || user.role === 'centre') {
        try {
          const res = await axios.get('/notifications/unread-count?type=lettre_mission');
          setUnreadLettresMission(res.data?.count || 0);
        } catch (error) {
        }
      }
    };
    
    fetchUnreadCount();
    const interval = setInterval(fetchUnreadCount, 300000);
    // Resync quand l'utilisateur revient sur l'onglet
    const onVisible = () => { if (document.visibilityState === 'visible') fetchUnreadCount(); };
    document.addEventListener('visibilitychange', onVisible);
    return () => {
      clearInterval(interval);
      document.removeEventListener('visibilitychange', onVisible);
    };
  }, [user]);

  // Fonction pour obtenir le bon chemin de profil selon le rôle
  const getProfilPath = () => {
    if (user.role === 'stagiaire') return '/profil';
    if (user.role === 'moniteur_vnm') return '/';
    if (user.role === 'tuteur') return '/profil-tuteur';
    if (user.role === 'entreprise') return '/profil-entreprise';
    if (user.role === 'formateur') return '/profil-formateur';
    if (user.role === 'responsable_pedagogique') return '/profil-responsable';
    if (user.role === 'centre') return '/profil-centre';
    if (user.role === 'candidat') return '/espace-candidat';
    if (user.role === 'evaluateur') return '/profil-evaluateur';
    return '/profil';
  };

  const GUIDE = { path: '/guide-bc1-bc2', icon: Compass, label: 'Guide BC1 / BC2' };
  const guide = guideOuvert ? [GUIDE] : [];

  const getNavItems = () => {
    
    const common = [
      { path: '/', icon: LayoutDashboard, label: 'Accueil' },
      { path: '/documents', icon: FileText, label: 'Documents' },
      { path: '/planning', icon: Calendar, label: 'Planning' },
      { path: getProfilPath(), icon: User, label: 'Profil' },
      { path: '/notifications', icon: MessageSquare, label: 'Messagerie', badge: unreadNotifications },
    ];

    if (user.role === 'centre') {
      return [
        { path: '/', icon: LayoutDashboard, label: 'Accueil' },
        { path: '/crm', icon: Briefcase, label: 'Business' },
        { path: '/gestion-candidats', icon: ClipboardCheck, label: 'Candidats' },
        { path: '/liste-stagiaires', icon: User, label: 'Stagiaires' },
        { path: '/anciens-stagiaires', icon: Archive, label: 'Anciens stagiaires' },
        { path: '/gestion-formateurs', icon: GraduationCap, label: 'Formateurs', badge: unreadLettresMission },
        { path: '/liste-tuteurs', icon: UserCheck, label: 'Tuteurs' },
        { path: '/liste-entreprises', icon: Building2, label: 'Entreprises' },
        { path: '/evaluateurs', icon: UserCheck, label: 'Évaluateurs' },
        { path: '/liste-rp', icon: GraduationCap, label: 'Resp. Pédagogiques' },
        { path: '/presence-centre', icon: ClipboardList, label: 'Présences' },
        { path: '/recapitulatif-autonomies', icon: Award, label: 'Autonomies' },
        { path: '/analytics', icon: BarChart3, label: 'Analytics' },
        { path: '/carnet-general', icon: BookOpen, label: 'Carnet de Notes' },
        GUIDE,
        { path: '/satisfaction', icon: Star, label: 'Satisfaction' },
        { path: '/qualiopi', icon: ShieldCheck, label: 'Qualiopi' },
        { path: '/relances', icon: Send, label: 'Relances' },
        { path: '/grille-epmsp', icon: ClipboardCheck, label: 'Grille EPMSP' },
        { path: '/exports', icon: Download, label: 'Exports' },
        { path: '/import', icon: Upload, label: 'Import BDD' },
        { path: '/formation-distance', icon: Monitor, label: 'Formation à distance' },
        { path: '/foad', icon: Play, label: 'Cours FOAD' },
        { path: '/newsletters', icon: Newspaper, label: 'Newsletters' },
        { path: '/agenda-formation', icon: BookOpen, label: 'Agenda Formation' },
        { path: '/rapport-journalier', icon: Send, label: 'Rapports Formateurs' },
        { path: '/planning', icon: Calendar, label: 'Planning' },
        { path: '/documents', icon: FileText, label: 'Documents' },
        { path: '/notifications', icon: MessageSquare, label: 'Messagerie', badge: unreadNotifications },
      ];
    }

    if (user.role === 'moniteur_vnm') {
      return [
        { path: '/', icon: LayoutDashboard, label: 'Mon Espace' },
        { path: '/annonces-emploi', icon: Briefcase, label: 'Annonces' },
        { path: '/notifications', icon: MessageSquare, label: 'Messagerie' },
      ];
    }

    if (user.role === 'stagiaire') {
      return [
        { path: '/', icon: LayoutDashboard, label: 'Accueil' },
        { path: '/mes-formations', icon: BookOpen, label: 'Mes Formations' },
        { path: '/documents', icon: FileText, label: 'Documents' },
        { path: '/planning', icon: Calendar, label: 'Planning' },
        { path: '/progression', icon: TrendingUp, label: 'Progression' },
        ...guide,
        { path: '/carnets-notes', icon: BookOpen, label: 'Carnets Notes' },
        { path: '/heures-stage', icon: Clock, label: 'Heures Stage' },
        { path: '/validation-autonomie-stagiaire', icon: ClipboardCheck, label: 'Mon Autonomie' },
        { path: '/notifications', icon: MessageSquare, label: 'Messagerie', badge: unreadNotifications },
        { path: '/reclamations', icon: MessageSquareWarning, label: 'Réclamation' },
      ];
    }

    if (user.role === 'formateur') {
      return [
        { path: '/', icon: LayoutDashboard, label: 'Accueil' },
        { path: '/documents', icon: FileText, label: 'Mes Documents' },
        { path: '/lettres-mission', icon: FileText, label: 'Lettres de Mission', badge: unreadLettresMission },
        { path: '/ma-charte', icon: ScrollText, label: 'Ma charte' },
        { path: '/foad', icon: Play, label: 'Cours FOAD' },
        { path: '/presence', icon: ClipboardList, label: 'Présence' },
        { path: '/notation', icon: CheckSquare, label: 'Noter' },
        { path: '/historique-notes', icon: History, label: 'Historique' },
        { path: '/carnet-general', icon: BookOpen, label: 'Carnet de Notes' },
        ...guide,
        { path: '/rapport-journalier', icon: Send, label: 'Rapport Journalier' },
        { path: '/planning', icon: Calendar, label: 'Planning' },
        { path: '/notifications', icon: MessageSquare, label: 'Messagerie', badge: unreadNotifications },
      ];
    }

    if (user.role === 'tuteur') {
      return [
        ...common.slice(0, 3),
        { path: '/presence', icon: ClipboardList, label: 'Présence' },
        { path: '/competences', icon: Award, label: 'Evaluer les compétences' },
        { path: '/progression', icon: TrendingUp, label: 'Progression' },
        { path: '/carnet-general', icon: BookOpen, label: 'Carnet de Notes' },
        ...common.slice(4),
        { path: '/reclamations', icon: MessageSquareWarning, label: 'Réclamation' },
      ];
    }

    if (user.role === 'entreprise') {
      return [
        { path: '/', icon: LayoutDashboard, label: 'Accueil' },
        { path: '/suivi-stagiaire', icon: TrendingUp, label: 'Suivi Stagiaire' },
        { path: '/presence-entreprise', icon: ClipboardList, label: 'Présence stagiaires' },
        { path: '/validation-autonomie', icon: ClipboardCheck, label: 'Valider Autonomie' },
        { path: '/bilan-mensuel', icon: FileEdit, label: 'Bilan Mensuel' },
        { path: '/planning', icon: Calendar, label: 'Planning' },
        { path: '/notifications', icon: MessageSquare, label: 'Messagerie', badge: unreadNotifications },
        { path: '/moniteurs', icon: Briefcase, label: 'Moniteurs VNM' },
        { path: '/mes-structures', icon: Building2, label: 'Mes Structures' },
        { path: '/reclamations', icon: MessageSquareWarning, label: 'Réclamation' },
      ];
    }

    if (user.role === 'responsable_pedagogique') {
      return [
        { path: '/', icon: LayoutDashboard, label: 'Accueil' },
        { path: '/crm', icon: Briefcase, label: 'Business' },
        { path: '/responsable/stagiaires', icon: Users, label: 'Stagiaires' },
        { path: '/responsable/formateurs', icon: Award, label: 'Formateurs' },
        { path: '/responsable/tuteurs', icon: UserCheck, label: 'Tuteurs' },
        { path: '/responsable/entreprises', icon: Building2, label: 'Entreprises' },
        { path: '/evaluateurs', icon: UserCheck, label: 'Évaluateurs' },
        { path: '/responsable/presences', icon: ClipboardList, label: 'Présences' },
        { path: '/responsable/analytics', icon: TrendingUp, label: 'Analytics' },
        { path: '/carnet-general', icon: BookOpen, label: 'Carnet de Notes' },
        ...guide,
        { path: '/grille-epmsp', icon: ClipboardCheck, label: 'Grille EPMSP' },
        { path: '/planning', icon: Calendar, label: 'Planning' },
        { path: '/documents', icon: FileText, label: 'Documents' },
        { path: '/notifications', icon: MessageSquare, label: 'Messagerie', badge: unreadNotifications },
      ];
    }

    // Candidat : accès limité
    if (user.role === 'candidat') {
      return [
        { path: '/espace-candidat', icon: LayoutDashboard, label: 'Mon Espace' },
        { path: '/notifications', icon: MessageSquare, label: 'Messagerie', badge: unreadNotifications },
      ];
    }

    // Evaluateur
    if (user.role === 'evaluateur') {
      return [
        { path: '/', icon: LayoutDashboard, label: 'Tableau de bord' },
        { path: '/profil-evaluateur', icon: User, label: 'Mon Profil' },
        { path: '/grille-epmsp', icon: ClipboardCheck, label: 'Grille EPMSP' },
        { path: '/notifications', icon: MessageSquare, label: 'Messagerie', badge: unreadNotifications },
      ];
    }

    return common;
  };

  const navItems = getNavItems();
  const sidebarInitiales = (
    <div className="w-10 h-10 rounded-full bg-[#8DC63F]/20 flex items-center justify-center text-[#8DC63F] font-bold text-sm flex-shrink-0" data-testid="sidebar-user-initials">
      {(user?.prenom?.[0] || '').toUpperCase()}{(user?.nom?.[0] || '').toUpperCase()}
    </div>
  );


  return (
    <div className="min-h-screen bg-slate-50">
      {/* Mobile header */}
      <div className="lg:hidden fixed top-0 left-0 right-0 h-16 bg-[#0F172A] flex items-center justify-between px-4 z-50">
        <div className="flex items-center space-x-3">
          <Anchor className="h-8 w-8 text-[#8DC63F]" />
          <span className="text-white font-display font-bold text-lg">FISL</span>
        </div>
        <Button
          variant="ghost"
          size="sm"
          onClick={() => setSidebarOpen(!sidebarOpen)}
          className="text-white hover:bg-slate-700"
          data-testid="mobile-menu-button"
        >
          {sidebarOpen ? <X className="h-6 w-6" /> : <Menu className="h-6 w-6" />}
        </Button>
      </div>

      {/* Sidebar */}
      <aside
        className={`
          fixed top-0 left-0 h-full w-64 bg-[#0F172A] text-white z-40
          transform transition-transform duration-300 ease-in-out flex flex-col
          lg:translate-x-0
          ${sidebarOpen ? 'translate-x-0' : '-translate-x-full'}
        `}
        data-testid="sidebar"
      >
        <div className="p-6 border-b border-slate-700 flex-shrink-0">
          <div className="flex items-center space-x-3">
            <img src="/logo-fisl.png" alt="FISL Logo" className="h-12 w-12 rounded object-contain bg-white p-1" />
            <div>
              <h1 className="font-display font-bold text-xl">FISL</h1>
              <p className="text-xs text-slate-400">Formation Motonautisme</p>
            </div>
          </div>
        </div>

        <div className="flex-1 overflow-y-auto p-4 scrollbar-thin">
          <Link 
            to={getProfilPath()}
            onClick={() => setSidebarOpen(false)}
            className="block mb-6 p-3 bg-slate-800/50 rounded-lg border border-slate-700 hover:bg-slate-700/50 hover:border-[#8DC63F] transition-all cursor-pointer"
            data-testid="user-info-link"
          >
            <div className="flex items-center gap-3">
              {['candidat', 'stagiaire'].includes(user.role) ? (
                <PhotoIdentite
                  userId={user.user_id}
                  alt={`${user.prenom} ${user.nom}`}
                  className="w-10 h-10 rounded-full object-cover flex-shrink-0"
                  testId="sidebar-user-photo"
                  fallback={sidebarInitiales}
                />
              ) : user.photo_identite ? (
                <img src={getAuthPhotoUrl(user.photo_identite)} alt={`${user.prenom} ${user.nom}`} className="w-10 h-10 rounded-full object-cover flex-shrink-0" data-testid="sidebar-user-photo" onError={(e) => { e.target.style.display = 'none'; if(e.target.nextSibling) e.target.nextSibling.style.display = 'flex'; }} />
              ) : sidebarInitiales}
              <div className="min-w-0">
                <p className="text-sm font-medium truncate">{user.prenom} {user.nom}</p>
                <p className="text-xs text-slate-400 capitalize">{roleLabelsMap[user.role] || user.role}</p>
              </div>
            </div>
          </Link>

          {/* Sélecteur de rôle multi-rôle */}
          {user.roles && user.roles.length > 1 && onSwitchRole && (
            <div className="mb-4 px-1" data-testid="role-switcher">
              <p className="text-xs text-slate-500 mb-1.5 uppercase tracking-wider">Rôle actif</p>
              <select
                value={user.role}
                onChange={(e) => onSwitchRole(e.target.value)}
                className="w-full px-3 py-2 rounded-lg text-sm bg-slate-800 border border-slate-600 text-white focus:outline-none focus:ring-2 focus:ring-[#8DC63F] focus:border-[#8DC63F] cursor-pointer"
                data-testid="role-switcher-select"
              >
                {user.roles.map(r => (
                  <option key={r} value={r}>{roleLabelsMap[r] || r}</option>
                ))}
              </select>
            </div>
          )}

          {/* Recherche globale pour Centre et Responsable Pédagogique */}
          {(user.role === 'centre' || user.role === 'responsable_pedagogique') && (
            <GlobalSearch onNavigate={() => setSidebarOpen(false)} />
          )}

          {/* Sélecteur de structure pour entreprises multi-structures */}
          {user.role === 'entreprise' && (
            <div className="px-4 py-2">
              <StructureSwitcher user={user} />
            </div>
          )}

          <nav className="space-y-1" data-testid="nav-menu">
            {navItems.map((item) => {
              const Icon = item.icon;
              const isActive = location.pathname === item.path;
              return (
                <Link
                  key={item.path}
                  to={item.path}
                  onClick={() => setSidebarOpen(false)}
                  className={`
                    flex items-center space-x-3 px-4 py-2 rounded-lg transition-all text-sm
                    ${isActive 
                      ? 'bg-[#8DC63F]/10 text-[#8DC63F] border-r-4 border-[#8DC63F]' 
                      : 'text-slate-300 hover:bg-slate-800 hover:text-white'
                    }
                  `}
                  data-testid={`nav-${item.label.toLowerCase().normalize('NFD').replace(/[\u0300-\u036f]/g, '').replace(/ /g, '-')}`}
                >
                  <div className="relative">
                    <Icon className={`h-5 w-5 ${item.badge > 9 ? 'animate-notification-bell' : ''}`} />
                    {item.badge > 0 && (
                      <span className={`absolute -top-2 -right-2 bg-red-500 text-white text-xs font-bold rounded-full shadow-lg shadow-red-500/50 flex items-center justify-center ${
                        item.badge > 9 ? 'w-7 h-7 text-sm animate-notification-bounce' : 'w-5 h-5 animate-pulse'
                      }`}>
                        {item.badge > 9 ? '9+' : item.badge}
                      </span>
                    )}
                  </div>
                  <span className="font-medium">{item.label}</span>
                </Link>
              );
            })}
          </nav>
        </div>

        <div className="flex-shrink-0 p-4 border-t border-slate-700">
          <Button
            onClick={onLogout}
            variant="ghost"
            className="w-full justify-start text-slate-300 hover:bg-red-500/10 hover:text-red-400"
            data-testid="logout-button"
          >
            <LogOut className="h-5 w-5 mr-3" />
            Déconnexion
          </Button>
        </div>
      </aside>

      {/* Main content */}
      <main className="lg:ml-64 min-h-screen pt-16 lg:pt-0 flex flex-col overflow-x-hidden">
        {/* Bandeau Mode Super Admin */}
        {isImpersonating && (
          <div className="bg-gradient-to-r from-amber-500 to-orange-500 text-white px-4 py-2 flex items-center justify-between shadow-md" data-testid="impersonation-banner">
            <div className="flex items-center gap-3">
              <Shield className="h-5 w-5" />
              <span className="font-medium">
                Mode Super Admin : Vue de {user?.prenom} {user?.nom} ({user?.role})
              </span>
            </div>
            <button
              onClick={onExitImpersonate}
              className="flex items-center gap-2 px-3 py-1 bg-white/20 hover:bg-white/30 rounded-lg transition-colors text-sm font-medium"
              data-testid="exit-impersonate-button"
            >
              <ArrowLeft className="h-4 w-4" />
              Retour au compte Centre
            </button>
          </div>
        )}
        <div className="p-4 sm:p-6 flex-1">
          {children}
        </div>
        <Footer />
      </main>

      {/* Overlay for mobile */}
      {sidebarOpen && (
        <div
          className="fixed inset-0 bg-black/50 z-30 lg:hidden"
          onClick={() => setSidebarOpen(false)}
        />
      )}
    </div>
  );
};

export default Layout;